'use client'
import { useState, useMemo, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLanguage } from '@/lib/context/LanguageContext'
import { socialPlatforms } from '@/lib/data/platforms'
import { messengerCards } from '@/lib/data/messengerCards'
import { FadeInUp } from '@/components/ui/FadeInUp'
import { RegistryIcon, type IconKey, iconRegistry } from '@/components/ui/iconRegistry'
import { brandMarks, BrandMark } from '@/components/ui/BrandMark'
import { toPersianNums } from '@/lib/persian'
import { onDark } from '@/lib/styles'
import type { PanelData } from './PlatformDetailPanel'

const KNOWN_ICONS: ReadonlySet<string> = new Set(Object.keys(iconRegistry))
const AUTO_MS = 10_000

const iranianCards = messengerCards.filter((m) => m.name !== 'Telegram' && m.name !== 'WhatsApp')

const FADE = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
  transition: { duration: 0.28, ease: 'easeOut' as const },
}

type MarqueeItem = { id: string; name: string; color: string }

function StatChip({ val, label, color }: { val: string; label: string; color?: string }) {
  return (
    <div
      className="rounded-2xl px-3 py-2.5 sm:px-5 sm:py-4 flex flex-col gap-1 items-center justify-center"
      style={{
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.09)',
      }}
    >
      <span
        className={`font-display font-black leading-tight ${val.length > 12 ? 'text-[11px] text-center w-full' : 'text-lg leading-none'}`}
        style={{ color: color ?? 'rgba(255,255,255,0.95)' }}
      >
        {val}
      </span>
      {label && <span className="text-[11px] text-white/40 leading-tight">{label}</span>}
    </div>
  )
}

export default function ChannelsHero() {
  const { t, lang } = useLanguage()
  const c = t.channels
  const fa = lang === 'fa'
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [paused, setPaused] = useState(false)

  function fmtVal(val: number, suf: string): string {
    if (!fa) return String(val) + suf
    const n = toPersianNums(String(val))
    const s = suf === 'M+' ? ' میلیون+' : suf === 'M' ? ' میلیون' : suf
    return n + s
  }

  const panelDataMap = useMemo<Map<string, PanelData>>(() => {
    const map = new Map<string, PanelData>()
    messengerCards.forEach((m) => {
      map.set(m.iconKey, {
        id: m.iconKey,
        name: fa ? m.nameFA : m.name,
        color: m.color,
        iconKey: m.iconKey,
        stat1: { val: fmtVal(m.stat1.val, m.stat1.suf), label: fa ? m.stat1.fa : m.stat1.en },
        stat2: { val: fmtVal(m.stat2.val, m.stat2.suf), label: fa ? m.stat2.fa : m.stat2.en },
        formats: fa ? m.formatsFA : m.formatsEN,
        highlight: fa ? m.highlightFA : m.highlightEN,
      })
    })
    socialPlatforms.forEach((p) => {
      if (!map.has(p.id)) {
        map.set(p.id, {
          id: p.id,
          name: fa ? p.nameFA : p.name,
          color: p.color,
          iconKey: p.id as IconKey,
          stat1: p.heroStat1
            ? {
                val: fa ? p.heroStat1.val.fa : p.heroStat1.val.en,
                label: fa ? p.heroStat1.label.fa : p.heroStat1.label.en,
              }
            : { val: fa ? p.stat.fa : p.stat.en, label: '' },
          stat2: p.heroStat2
            ? {
                val: fa ? p.heroStat2.val.fa : p.heroStat2.val.en,
                label: fa ? p.heroStat2.label.fa : p.heroStat2.label.en,
              }
            : undefined,
          formats: p.heroFormats ? (fa ? p.heroFormats.fa : p.heroFormats.en) : undefined,
          highlight: p.heroHighlight
            ? fa
              ? p.heroHighlight.fa
              : p.heroHighlight.en
            : fa
              ? p.desc.fa
              : p.desc.en,
        })
      }
    })
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang])

  const marqueeItems = useMemo<MarqueeItem[]>(
    () => [
      ...socialPlatforms.map((p) => ({ id: p.id, name: fa ? p.nameFA : p.name, color: p.color })),
      ...iranianCards.map((m) => ({ id: m.iconKey, name: fa ? m.nameFA : m.name, color: m.color })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang],
  )

  // Auto-rotation: initialise on mount, restart timer on every selection change.
  // Pauses while the panel is hovered/focused, and never rotates for users who
  // asked for reduced motion (framer only disables transitions, not the swaps).
  useEffect(() => {
    const ids = marqueeItems.map((m) => m.id)
    if (ids.length === 0) return

    if (!selectedId || !ids.includes(selectedId)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedId(ids[0])
      return
    }

    if (paused || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const timer = setTimeout(() => {
      setSelectedId((prev) => {
        const idx = ids.indexOf(prev ?? '')
        return ids[(idx + 1) % ids.length]
      })
    }, AUTO_MS)

    return () => clearTimeout(timer)
  }, [selectedId, marqueeItems, paused])

  const selected = selectedId ? (panelDataMap.get(selectedId) ?? null) : null

  const defaultHighlights = [
    { val: '31', label: c.h1 },
    { val: '10+', label: c.h2 },
    { val: '2,500+', label: c.h3 },
    { val: '1,000+', label: c.h4 },
  ]

  return (
    <FadeInUp className="mb-4 md:mb-6">
      <div
        className="rounded-3xl overflow-hidden relative"
        style={{
          background: 'linear-gradient(145deg, #0a1628 0%, #06101f 100%)',
          border: '1px solid rgba(255,255,255,0.07)',
        }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
      >
        {/* AUTO_MS progress bar — resets on every platform change; hidden while
            paused (hover/focus) so it always mirrors the actual rotation timer */}
        <AnimatePresence>
          {selected && !paused && (
            <motion.div
              key={selectedId + '-bar'}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: AUTO_MS / 1000, ease: 'linear', opacity: { duration: 0.2 } }}
              className="absolute top-0 left-0 right-0 h-[2px] z-20 origin-left rtl:origin-right"
              style={{ background: selected.color }}
            />
          )}
        </AnimatePresence>

        {/* ── TOP: 7/5 split ── */}
        <div className="grid lg:grid-cols-12 items-stretch h-auto lg:h-[420px]">
          {/* LEFT 7: platform logo only */}
          <div className="lg:col-span-7 border-b lg:border-b-0 lg:border-l border-white/[0.06] flex items-center justify-center relative min-h-[150px] overflow-hidden">
            {/* ambient glow morphs to platform color */}
            <AnimatePresence>
              <motion.div
                key={selected?.id ?? 'none'}
                className="absolute inset-0 pointer-events-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.7 }}
                style={{
                  background: selected
                    ? `radial-gradient(ellipse at 50% 50%, ${selected.color}38 0%, transparent 65%)`
                    : 'radial-gradient(ellipse at 50% 50%, rgba(0,180,216,0.09) 0%, transparent 65%)',
                }}
              />
            </AnimatePresence>

            {/* dot-grid texture */}
            <div
              className="absolute inset-0 pointer-events-none opacity-[0.03]"
              style={{
                backgroundImage:
                  'radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)',
                backgroundSize: '24px 24px',
              }}
            />

            <AnimatePresence mode="wait">
              {!selected ? (
                /* dim icon grid while loading */
                <motion.div
                  key="placeholder"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="relative z-10 grid grid-cols-3 gap-3"
                >
                  {socialPlatforms.map((p) => (
                    <div
                      key={p.id}
                      className="w-12 h-12 rounded-2xl flex items-center justify-center opacity-[0.15]"
                      style={{ background: `${p.color}18`, color: p.color }}
                    >
                      {KNOWN_ICONS.has(p.id) && (
                        <RegistryIcon name={p.id as IconKey} className="w-7 h-7" />
                      )}
                    </div>
                  ))}
                </motion.div>
              ) : (
                /* large platform logo */
                <motion.div
                  key={selected.id + '-logo'}
                  initial={{ opacity: 0, scale: 0.55, rotate: -12 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  exit={{ opacity: 0, scale: 0.65, rotate: 10 }}
                  transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
                  className="relative z-10"
                >
                  <div
                    className="w-24 h-24 md:w-36 md:h-36 lg:w-44 lg:h-44 rounded-3xl flex items-center justify-center"
                    style={{
                      background: `${selected.color}18`,
                      border: `1px solid ${selected.color}28`,
                    }}
                  >
                    {/* stage shows the ORIGINAL full-color mark (recognition is
                        the job here); the chip strip below stays monochrome */}
                    {brandMarks[selected.id] ? (
                      <BrandMark
                        id={selected.id}
                        className="w-14 h-14 md:w-20 md:h-20 lg:w-24 lg:h-24"
                      />
                    ) : (
                      <div style={{ color: onDark(selected.color) }}>
                        <RegistryIcon
                          name={selected.iconKey}
                          className="w-14 h-14 md:w-20 md:h-20 lg:w-24 lg:h-24"
                        />
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* RIGHT 5: all platform data */}
          <div className="lg:col-span-5 p-4 md:p-8 lg:p-10 flex flex-col justify-center min-h-[200px] overflow-hidden">
            <AnimatePresence mode="wait">
              {!selected ? (
                <motion.div key="default-data" {...FADE} className="flex flex-col gap-5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/25">
                    {c.section}
                  </p>
                  <h2 className="font-display font-black text-white text-3xl md:text-4xl leading-tight">
                    {c.heading}
                  </h2>
                  <div className="grid grid-cols-2 gap-3">
                    {defaultHighlights.map((item) => (
                      <StatChip
                        key={item.val}
                        val={fa ? toPersianNums(item.val) : item.val}
                        label={item.label}
                      />
                    ))}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key={selected.id + '-data'}
                  {...FADE}
                  className="flex flex-col gap-3 sm:gap-4"
                >
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/25">
                    {fa ? 'پلتفرم فعال' : 'Active Platform'}
                  </p>
                  <h2
                    className="font-display font-black text-2xl md:text-3xl leading-tight"
                    style={{ color: onDark(selected.color) }}
                  >
                    {selected.name}
                  </h2>

                  {/* stat chips */}
                  <div className="grid grid-cols-2 gap-3">
                    <StatChip
                      val={selected.stat1.val}
                      label={selected.stat1.label}
                      color={onDark(selected.color)}
                    />
                    {selected.stat2 && (
                      <StatChip
                        val={selected.stat2.val}
                        label={selected.stat2.label}
                        color={onDark(selected.color)}
                      />
                    )}
                  </div>

                  {/* format pills */}
                  {selected.formats && selected.formats.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {selected.formats.map((fmt) => (
                        <span
                          key={fmt}
                          className="text-[10px] font-semibold px-2.5 py-1 rounded-full"
                          style={{
                            background: `${selected.color}15`,
                            border: `1px solid ${selected.color}25`,
                            color: onDark(selected.color),
                          }}
                        >
                          {fmt}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* description callout */}
                  <div
                    className="rounded-2xl overflow-hidden"
                    style={{ border: `1px solid ${selected.color}28` }}
                  >
                    <div
                      className="h-[2px]"
                      style={{
                        background: `linear-gradient(90deg, ${selected.color} 0%, transparent 100%)`,
                      }}
                    />
                    <div
                      className="px-4 py-2 sm:py-3 flex gap-3 items-start"
                      style={{ background: `${selected.color}0c` }}
                    >
                      <span
                        className="text-[10px] font-black flex-shrink-0 mt-0.5"
                        style={{ color: onDark(selected.color) }}
                      >
                        ✦
                      </span>
                      <p
                        className="text-[11px] leading-relaxed"
                        style={{ color: 'rgba(255,255,255,0.58)' }}
                      >
                        {selected.highlight}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* ── BOTTOM: platform chip strip ── */}
        <div className="border-t border-white/[0.06] px-4 md:px-8 py-3 md:py-4">
          <ul className="flex flex-wrap gap-2 py-1">
            {marqueeItems.map((item) => {
              const isSelected = selectedId === item.id
              return (
                <li key={item.id} className="flex-shrink-0">
                  <motion.button
                    onClick={() => setSelectedId(item.id)}
                    aria-pressed={isSelected}
                    whileHover={{ scale: 1.06, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="inline-flex items-center gap-1.5 h-8 sm:h-9 px-2.5 sm:px-3.5 rounded-full border text-[10px] sm:text-[11px] font-semibold whitespace-nowrap cursor-pointer"
                    style={{
                      background: isSelected ? `${item.color}20` : 'rgba(255,255,255,0.07)',
                      borderColor: isSelected ? `${item.color}55` : 'rgba(255,255,255,0.15)',
                      color: isSelected ? onDark(item.color) : 'rgba(255,255,255,0.6)',
                      boxShadow: isSelected ? `0 0 12px ${item.color}30` : 'none',
                    }}
                  >
                    {KNOWN_ICONS.has(item.id) && (
                      <div
                        className="w-4 h-4 flex-shrink-0"
                        style={{ color: isSelected ? onDark(item.color) : 'rgba(255,255,255,0.5)' }}
                      >
                        <RegistryIcon name={item.id as IconKey} className="w-4 h-4" />
                      </div>
                    )}
                    {item.name}
                  </motion.button>
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    </FadeInUp>
  )
}
